import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { FORMATIONS } from "../sport/formation";
import Avatar from "./Avatar";
import EditProfileModal from "./EditProfileModal";
import { StandardIcon, RankedIcon, DailyIcon } from "./modeIcons";
import CloseIcon from "./icons";
import TierBadge from "./TierBadge";
import { getTier } from "../sport/tiers";
import SquadList from "../sport/components/SquadList";
import SPORT from "../sport/config";

const RUNS_PAGE = 9;

function formatDateTime(iso) {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} · ${time}`;
}

function BestRunCard({ title, icon: Icon, run }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex flex-col">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 grid place-items-center">
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
      </div>

      {run ? (
        <div className="flex items-baseline gap-3">
          <div className="text-xs tabular-nums">
            <span className="text-emerald-400 font-semibold">{run.wins}</span>
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-zinc-400">{run.draws}</span>
            <span className="text-zinc-700 mx-1">·</span>
            <span className="text-red-400">{run.losses}</span>
          </div>
          <span className="text-sm font-bold text-emerald-400 ml-auto">
            {run.rating}
          </span>
        </div>
      ) : (
        <div className="text-xs text-zinc-500">No runs yet</div>
      )}
    </div>
  );
}

function RankedCard({ entry, record }) {
  const played = record ? record.w + record.d + record.l : 0;
  const tier = entry ? getTier(entry.glicko_rating) : null;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex flex-col">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 grid place-items-center">
          <RankedIcon className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-semibold text-zinc-100">Ranked</h3>
        {entry && (
          <span className="ml-auto">
            <TierBadge sr={entry.glicko_rating} />
          </span>
        )}
      </div>
      {entry ? (
        <div className="flex items-baseline gap-3">
          <div className="flex items-baseline gap-1.5">
            <span
              className={`text-2xl font-bold tabular-nums leading-none ${tier?.text || "text-emerald-400"}`}
            >
              {Math.round(entry.glicko_rating)}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              SR
            </span>
          </div>
          {played > 0 && (
            <div className="text-xs tabular-nums ml-auto">
              <span className="text-emerald-400 font-semibold">{record.w}</span>
              <span className="text-zinc-600">W</span>
              {record.d > 0 && (
                <>
                  <span className="text-zinc-700 mx-1">·</span>
                  <span className="text-zinc-400 font-semibold">
                    {record.d}
                  </span>
                  <span className="text-zinc-600">D</span>
                </>
              )}
              <span className="text-zinc-700 mx-1">·</span>
              <span className="text-red-400 font-semibold">{record.l}</span>
              <span className="text-zinc-600">L</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-zinc-500">Unranked</div>
      )}
    </div>
  );
}

function RunRow({ run, expanded, onToggle }) {
  const slots = FORMATIONS[run.formation] || null;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-zinc-900/70 transition"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2">
          {run.mode === "ranked" && (
            <span className="text-[9px] uppercase tracking-widest text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">
              Ranked
            </span>
          )}
          <span className="text-xs text-zinc-500 truncate">
            {formatDateTime(run.created_at)}
          </span>
        </div>
        <div className="text-xs tabular-nums shrink-0 flex items-center gap-1.5">
          <span className="text-emerald-400 font-semibold">{run.wins}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-400">{run.draws}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-red-400">{run.losses}</span>
        </div>
        {run.mode === "ranked" && run.glicko_delta != null && (
          <span
            className={`text-xs font-bold tabular-nums shrink-0 w-12 text-right ${
              run.glicko_delta >= 0 ? "text-emerald-400" : "text-red-400"
            }`}
          >
            {run.glicko_delta >= 0
              ? `+${Math.round(run.glicko_delta)}`
              : Math.round(run.glicko_delta)}
          </span>
        )}
        <span className="text-sm font-bold text-emerald-400 w-10 text-right tabular-nums shrink-0">
          {run.rating}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`w-4 h-4 text-zinc-500 transition-transform shrink-0 ${
            expanded ? "rotate-90" : ""
          }`}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 px-4 py-3">
          {slots ? (
            <SquadList roster={run.roster} slots={slots} />
          ) : (
            <div className="text-xs text-zinc-500">
              Formation data unavailable for this run.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProfileModal({ open, onClose }) {
  const { user, profile, signOut } = useAuth();
  const [editing, setEditing] = useState(false);
  const [runs, setRuns] = useState(null); // null = loading
  const [runsHasMore, setRunsHasMore] = useState(false);
  const [runsLoadingMore, setRunsLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [bestEntries, setBestEntries] = useState({});
  const [rankedRecord, setRankedRecord] = useState(null);
  //const [rankedRank, setRankedRank] = useState(null);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Load best-per-mode (bounded) + first page of runs when opened
  useEffect(() => {
    if (!open || !user) {
      setRuns(null);
      setBestEntries({});
      setExpandedId(null);
      setRunsHasMore(false);
      return;
    }
    let cancelled = false;

    supabase
      .from("leaderboard_entries")
      .select("mode, wins, draws, losses, rating, points, glicko_rating")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data || []).forEach((e) => (map[e.mode] = e));
        setBestEntries(map);
      });

    supabase
      .from("runs")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(0, RUNS_PAGE - 1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("Failed to load runs:", error);
          setRuns([]);
          setRunsHasMore(false);
          return;
        }
        setRuns(data || []);
        setRunsHasMore((data || []).length === RUNS_PAGE);
      });

    supabase
      .from("matches")
      .select("player1, player2, outcome")
      .eq("status", "complete")
      .then(({ data }) => {
        if (cancelled) return;
        let w = 0,
          d = 0,
          l = 0;
        (data || []).forEach((m) => {
          if (!m.outcome) return;
          if (m.outcome === "tie") {
            d++;
          } else {
            const won =
              (m.outcome === "p1" && m.player1 === user.id) ||
              (m.outcome === "p2" && m.player2 === user.id);
            won ? w++ : l++;
          }
        });
        setRankedRecord({ w, d, l });
      });

    // supabase
    //   .rpc("get_leaderboard_rank", { p_mode: "ranked", p_user: user.id })
    //   .then(({ data }) => {
    //     if (!cancelled) setRankedRank(data ?? null);
    //   });

    return () => {
      cancelled = true;
    };
  }, [open, user]);

  const loadMoreRuns = async () => {
    if (!user || !runs) return;
    setRunsLoadingMore(true);
    const { data, error } = await supabase
      .from("runs")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(runs.length, runs.length + RUNS_PAGE - 1);
    if (!error) {
      setRuns((prev) => [...prev, ...(data || [])]);
      setRunsHasMore((data || []).length === RUNS_PAGE);
    }
    setRunsLoadingMore(false);
  };

  if (!open || !user) return null;

  const username = profile?.username || user.email?.split("@")[0] || "you";
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md grid place-items-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-3xl
                   max-h-[92vh] overflow-y-auto scrollbar-fancy
                   p-8 animate-scale-in shadow-2xl shadow-black/50 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-4 right-4 flex items-center gap-1 z-10">
          <button
            onClick={() => {
              signOut();
              onClose?.();
            }}
            className="w-9 h-9 grid place-items-center rounded-full text-zinc-500
               hover:text-red-300 hover:bg-zinc-900 transition"
            aria-label="Sign out"
            title="Sign out"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
          <button
            onClick={() => setEditing(true)}
            className="w-9 h-9 grid place-items-center rounded-full text-zinc-500
                       hover:text-zinc-100 hover:bg-zinc-900 transition"
            aria-label="Edit profile"
            title="Edit profile"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-4 h-4"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full text-zinc-500
                       hover:text-zinc-100 hover:bg-zinc-900 transition"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Profile header */}
        <div className="flex items-center gap-5 mb-8">
          <Avatar
            username={profile?.username}
            email={user.email}
            url={profile?.avatar_url}
            size={72}
          />
          <div className="min-w-0">
            <div className="text-2xl font-bold text-zinc-100 truncate">
              {username}
            </div>
            {user.email && (
              <div className="text-sm text-zinc-500 truncate">{user.email}</div>
            )}
            {memberSince && (
              <div className="text-xs text-zinc-600 mt-1">
                Member since {memberSince}
              </div>
            )}
          </div>
        </div>

        {/* Best runs */}
        <div className="mb-8">
          {/* <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
            Best runs
          </div> */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <BestRunCard
              title="Standard"
              icon={StandardIcon}
              run={bestEntries.standard || null}
            />
            <RankedCard
              entry={bestEntries.ranked || null}
              record={rankedRecord}
            />
            <BestRunCard
              title="Daily Challenge"
              icon={DailyIcon}
              run={bestEntries.daily || null}
            />
          </div>
        </div>

        {/* Runs */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
            History
          </div>

          {runs === null ? (
            <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl py-8 grid place-items-center">
              <div className="text-sm text-zinc-500 animate-pulse">
                Loading…
              </div>
            </div>
          ) : runs.length === 0 ? (
            <div className="bg-zinc-900/30 border border-zinc-800 border-dashed rounded-xl py-12 grid place-items-center">
              <div className="text-center">
                <div className="text-sm text-zinc-400 mb-1">
                  No runs saved yet
                </div>
                <div className="text-xs text-zinc-600">
                  Completed {SPORT.squadName}s will show up here once you
                  finish a game
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {runs.map((run) => (
                <RunRow
                  key={run.id}
                  run={run}
                  expanded={expandedId === run.id}
                  onToggle={() =>
                    setExpandedId(expandedId === run.id ? null : run.id)
                  }
                />
              ))}

              {runsHasMore && (
                <div className="pt-1 flex justify-center">
                  <button
                    onClick={loadMoreRuns}
                    disabled={runsLoadingMore}
                    className="px-5 py-2 rounded-full bg-zinc-900 border border-zinc-800 text-sm font-semibold text-zinc-300 hover:border-zinc-700 transition disabled:opacity-50"
                  >
                    {runsLoadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <EditProfileModal open={editing} onClose={() => setEditing(false)} />
    </div>
  );
}
